import React, { useMemo } from "react"
import { cva } from "class-variance-authority"
import Spinner from "./Spinner"
import cn from "../../../utils/classes"
import Typography, { typographyTypes } from "./Typography"

export type ButtonType =
  | "primary"
  | "secondary"
  | "alert"
  | "purpleBrand"
  | "purpleBrandInverse"
  | "tertiary"
  | "transparent"
  | "white"
export type ButtonSize = "xs" | "sm" | "base" | "lg" | "xl"

export interface ButtonProps {
  id?: string
  buttonLabel?: React.ReactNode
  buttonSize?: ButtonSize
  disabled?: boolean
  type?: ButtonType
  roundedFull?: boolean
  onClick?: () => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void
  loading?: boolean
  onFocus?: () => void
  rootStyleOverrides?: string
  textBold?: boolean
  tabIndex?: number
  styleOverrides?: string
  typographyStyleOverrides?: string
  ariaLabel?: string
  ariaDisabled?: boolean
  readOnly?: boolean
}

/**
 * `Button` is a customizable button component.
 *
 * @component
 * @example
 * // Example usage of Button
 * <Button buttonLabel="Click Me" buttonSize="lg" type="primary" onClick={() => console.log("Button clicked!")}/>
 *
 * @param {object} props - The properties that define the Button component.
 * @param {React.ReactNode} [props.buttonLabel='Submit'] - The label of the button.
 * @param {("xs" | "sm" | "base" | "lg" | "xl")} [props.buttonSize='lg'] - The size of the button.
 * @param {boolean} [props.disabled=false] - If true, the button will be disabled.
 * @param {("primary"|"secondary"|"alert"|"purpleBrand"|"purpleBrandInverse"|"tertiary"|"transparent")} [props.type='primary'] - The type of the button.
 * @param {boolean} [props.roundedFull=true] - If true, the button will have fully rounded corners.
 * @param {function} props.onClick - The function to call when the button is clicked.
 * @param {function} props.onKeyDown - The function to call when a key is pressed while the button is focused.
 * @param {function} props.onFocus - The function to call when the button has the focus.
 * @param {string} props.rootStyleOverrides - Override styles for the root container surronding the button.
 * @param {boolean} props.textBold - If true, the button text will be bold.
 * @param {string} props.styleOverrides - Override styles for the button.
 * @param {string} props.typographyStyleOverrides - Override styles for the button text.
 * @param {string} props.ariaLabel - The aria-label attribute for the button for accessibility.
 * @param {boolean} props.ariaDisabled - If true, the button will be marked as disabled for accessibility.
 *
 * @returns {React.Component} The Button component.
 */
export const Button: React.FC<ButtonProps> = ({
  id,
  buttonLabel = "Submit",
  buttonSize = "lg",
  disabled = false,
  type = "primary",
  roundedFull = true,
  onClick,
  onKeyDown,
  loading = false,
  onFocus,
  textBold,
  styleOverrides,
  rootStyleOverrides,
  typographyStyleOverrides,
  ariaLabel,
  ariaDisabled,
  readOnly,
  tabIndex,
}) => {
  // TODO (NF): hoist these to other components as needed (button/button wrapper both come to mind first)
  //       will do this in my next PR when I start integrating with affiliate-web
  const focusAndActiveStyle =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2198CC] focus-visible:ring-offset-2 focus-visible:ring-offset-white active:outline-none active:ring-2 active:ring-[#2198CC] active:ring-offset-2 active:ring-offset-white"
  const disabledButtonStyle =
    "cursor-not-allowed border-gray-400 bg-gray-200 hover:bg-gray-200"
  const readOnlyButtonStyle = "cursor-not-allowed"
  const buttonVariants = cva(
    [
      "w-full",
      "border-solid",
      "border-1/4",
      "font-medium",
      "cursor-pointer",
      "font-display",
      "text-4",
    ],
    {
      variants: {
        type: {
          primary: `border-blue-500 bg-primary-blue hover:bg-secondary-blue ${focusAndActiveStyle}`,
          secondary: "border-gray-400 bg-gray-200 hover:bg-gray-300",
          alert:
            "border-outline-red bg-red-700 hover:bg-red-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-700 focus:ring-offset-white",
          purpleBrand:
            "border-purple-brand bg-purple-brand hover:bg-secondary-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-purple",
          purpleBrandInverse: "border-white bg-white hover:bg-gray-100",
          tertiary:
            "border-solid border-1/4 border-gray-300 bg-white hover:bg-gray-300",
          transparent: `border-solid border-1/4 border-gray-300 bg-transparent hover:bg-gray-100 ${focusAndActiveStyle}`,
          white: `border-solid border-1/4 border-gray-300 bg-white ${focusAndActiveStyle}`,
        },
        buttonSize: {
          xs: "px-[11px] py-[7px]",
          sm: "px-[13px] py-[9px]",
          base: "px-[17px] py-[9px]",
          lg: "px-[17px] py-[8.09px]",
          xl: "px-[25px] py-[13px]",
        },
        disabled: {
          true: disabledButtonStyle,
        },
        ariaDisabled: {
          true: disabledButtonStyle,
        },
        readOnly: {
          true: readOnlyButtonStyle,
        },
        roundedFull: {
          true: "rounded-full",
          false: "rounded",
        },
      },
    },
  )

  const disabledTypographyStyle = "text-gray-400"
  const typographyVariants = cva([], {
    variants: {
      type: {
        primary: "text-white",
        secondary: "text-black",
        alert: "text-white",
        purpleBrand: "text-white",
        purpleBrandInverse: "text-purple-brand",
        tertiary: "text-gray-700",
        transparent: "text-gray-700",
        white: "text-gray-700",
      },
      buttonSize: {
        xs: "text-xs leading-4",
        sm: "text-sm !leading-4",
        base: "text-sm leading-5",
        lg: "text-base leading-6",
        xl: "text-base leading-6",
      },
      disabled: {
        true: disabledTypographyStyle,
      },
      ariaDisabled: {
        true: disabledTypographyStyle,
      },
      textBold: {
        true: "font-bold",
      },
    },
  })

  // Note: the root container needs a z-index to create it's own stacking context. Otherwise it will join the parent stacking context and cause unforseen issues.
  const rootVariants = cva(["w-fit", "z-0"], {
    variants: {},
  })

  const className = useMemo(() => {
    return cn(
      buttonVariants({
        buttonSize,
        type,
        disabled,
        ariaDisabled,
        roundedFull,
        readOnly,
      }),
      styleOverrides,
      "minimum-touchtarget",
    )
  }, [
    type,
    buttonSize,
    disabled,
    ariaDisabled,
    roundedFull,
    styleOverrides,
    buttonVariants,
    readOnly,
  ])
  const rootClassName = useMemo(() => {
    return cn(rootVariants({}), rootStyleOverrides)
  }, [rootVariants, rootStyleOverrides])

  const typographyClassName = useMemo(
    () =>
      cn(
        typographyVariants({
          buttonSize,
          type,
          disabled,
          textBold,
        }),
        typographyStyleOverrides,
      ),
    [
      buttonSize,
      disabled,
      textBold,
      type,
      typographyStyleOverrides,
      typographyVariants,
    ],
  )

  const isLoading = loading === true

  // If loading, disable all interaction
  const effectiveDisabled = disabled || isLoading || ariaDisabled
  const effectiveTabIndex = isLoading
    ? -1
    : typeof tabIndex === "number"
      ? tabIndex
      : undefined
  const handleClick = effectiveDisabled || readOnly ? undefined : onClick
  const handleFocus = effectiveDisabled || readOnly ? undefined : onFocus
  const handleKeyDown = effectiveDisabled || readOnly ? undefined : onKeyDown

  const useAriaDisabledOrReadOnly = (
    ariaDisabled?: boolean,
    readOnly?: boolean,
  ) => {
    return useMemo(
      () => ({
        "aria-disabled": ariaDisabled || readOnly,
      }),
      [ariaDisabled, readOnly],
    )
  }

  const buttonAriaProps = useAriaDisabledOrReadOnly(effectiveDisabled, readOnly)
  return (
    <div className={rootClassName}>
      <button
        id={id}
        className={className + (isLoading ? " relative" : "")}
        disabled={disabled}
        onClick={handleClick}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
        tabIndex={effectiveTabIndex}
        {...buttonAriaProps}
      >
        {isLoading && (
          <span
            className="absolute inset-0 flex items-center justify-center bg-white/70 z-10"
            style={{ borderRadius: "inherit" }}
          >
            <Spinner
              size="xs"
              text=""
              parentStyleOverrides="h-full w-full flex items-center justify-center"
              containerStyleOverrides=""
            />
          </span>
        )}
        <span
          className={isLoading ? "opacity-50 pointer-events-none" : undefined}
        >
          <Typography
            text={buttonLabel}
            variant={typographyTypes.NORMAL}
            styleOverrides={typographyClassName}
          />
        </span>
      </button>
    </div>
  )
}

export default Button
